import { useEffect } from 'react';

/**
 * useSeo — set the current page's <title>, meta description, OG/Twitter
 * tags, canonical URL, and optional JSON-LD blocks. Idempotent: on unmount
 * it restores whatever was there before so navigating back to another
 * page inherits that page's own tags cleanly.
 *
 * The pattern mirrors HammerLanding.jsx — no react-helmet dependency, works
 * with any React Router route.
 *
 * @param {object} opts
 * @param {string} opts.title              Page <title>
 * @param {string} opts.description        Meta description (also used for OG/Twitter)
 * @param {string} opts.canonical          Absolute canonical URL for this page
 * @param {Array<{id: string, data: object}>} [opts.jsonLd]  Extra JSON-LD blocks
 */
export default function useSeo({ title, description, canonical, jsonLd = [] }) {
  useEffect(() => {
    const managed = [];
    const prevTitle = document.title;
    if (title) document.title = title;

    const upsertMeta = (attr, key, content) => {
      let el = document.head.querySelector(`meta[${attr}="${key}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, key);
        document.head.appendChild(el);
        managed.push(el);
      }
      const prev = el.getAttribute('content');
      el.setAttribute('content', content);
      return () => {
        if (prev != null) el.setAttribute('content', prev);
      };
    };

    const restores = [];
    if (description) {
      restores.push(upsertMeta('name', 'description', description));
      restores.push(upsertMeta('property', 'og:description', description));
      restores.push(upsertMeta('name', 'twitter:description', description));
    }
    if (title) {
      restores.push(upsertMeta('property', 'og:title', title));
      restores.push(upsertMeta('name', 'twitter:title', title));
    }

    let prevCanonical = null;
    let canonicalEl = null;
    if (canonical) {
      restores.push(upsertMeta('property', 'og:url', canonical));
      canonicalEl = document.head.querySelector('link[rel="canonical"]');
      prevCanonical = canonicalEl ? canonicalEl.getAttribute('href') : null;
      if (!canonicalEl) {
        canonicalEl = document.createElement('link');
        canonicalEl.setAttribute('rel', 'canonical');
        document.head.appendChild(canonicalEl);
        managed.push(canonicalEl);
      }
      canonicalEl.setAttribute('href', canonical);
    }

    // JSON-LD blocks — the ones from index.html stay; these are additive
    // and get removed on unmount so they don't leak between routes.
    jsonLd.forEach(({ id, data }) => {
      if (!id || !data) return;
      // Skip if already present (avoids duplicate blocks on re-render)
      if (document.getElementById(id)) return;
      const el = document.createElement('script');
      el.type = 'application/ld+json';
      el.id = id;
      el.textContent = JSON.stringify(data);
      document.head.appendChild(el);
      managed.push(el);
    });

    return () => {
      if (title) document.title = prevTitle;
      restores.forEach((fn) => fn && fn());
      if (canonicalEl && prevCanonical) canonicalEl.setAttribute('href', prevCanonical);
      managed.forEach((el) => el.parentNode && el.parentNode.removeChild(el));
    };
  }, [title, description, canonical, jsonLd]);
}
